import React, { useEffect, useRef, useState } from "react";
import { shallowEqual, useSelector } from "react-redux";
import moment from "moment";
import { useLocation } from "react-router-dom";

import { createStyles, makeStyles, Theme } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import Grid from "@material-ui/core/Grid";

import StoreStateModel from "../../../../Utils/Models/store";
import { convertISOStrToLocaleTimeStr } from "../../../../Pages/Cameras/utilities";

const THUMBNAIL_INTERVALS_SECONDS = 20;
const IMAGE_WIDTH = 100;
const TIMESTAMP_HEIGHT = 30;
const IMAGE_BORDER_WIDTH = 2;

interface IThemeProps {
  positionLeft: number;
  positionTop: number;
  imageHeight: number;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    imgWrapper: {
      position: "absolute",
      left: ({ positionLeft }: IThemeProps) => positionLeft - IMAGE_WIDTH / 2,
      top: ({ positionTop, imageHeight }: IThemeProps) =>
        positionTop - imageHeight - TIMESTAMP_HEIGHT - IMAGE_BORDER_WIDTH,
    },
    timeWrapper: {
      backgroundColor: "rgba(0, 0, 0, .75)",
      borderWidth: "2px",
      borderColor: "black",
      borderRadius: "5px",
      position: "absolute",
      textAlign: "center",
      width: IMAGE_WIDTH,
      left: ({ positionLeft }: IThemeProps) => positionLeft - IMAGE_WIDTH / 2,
      top: ({ positionTop }: IThemeProps) => positionTop - TIMESTAMP_HEIGHT,
    },
    imagePreload: {
      width: IMAGE_WIDTH,
    },
    imageNone: {
      display: "none",
    },
    image: {
      borderWidth: `${IMAGE_BORDER_WIDTH}px`,
      borderColor: "white",
      borderRadius: "5px",
      borderStyle: "solid",
      width: IMAGE_WIDTH,
    },
  })
);

interface Props {
  realtimeSecondsElapsed: number;
  startDateTime: string;
  timezone?: string;
  s3: any;
  positionLeft: number;
  positionTop: number;
  videoAspectRatio: number;
  serialNumber: string;
  showImage: boolean;
}

const Thumbnail = ({
  realtimeSecondsElapsed,
  startDateTime,
  timezone,
  s3,
  positionLeft,
  positionTop,
  videoAspectRatio,
  serialNumber,
  showImage,
}: Props) => {
  const classes = useStyles({
    positionLeft,
    positionTop,
    imageHeight: IMAGE_WIDTH / videoAspectRatio,
  });
  const location: any = useLocation();
  const accountId = useSelector((state: StoreStateModel) => state.auth.accountId, shallowEqual);
  const [imgUrl, setImgUrl] = useState("");
  const [nextImgUrl, setNextImgUrl] = useState("");
  const [timestampStr, setTimestampStr] = useState("");
  const [displayTime, setDisplayTime] = useState("");
  const [loading, setLoading] = useState(false);
  const imgRef = useRef() as React.MutableRefObject<HTMLImageElement>;

  useEffect(() => {
    if (!s3) {
      return;
    }

    if (!serialNumber) {
      return;
    }

    if (!accountId) {
      return;
    }

    const timestamp = moment(startDateTime).add(realtimeSecondsElapsed, "seconds");
    const timestampStr =
      moment(timestamp)
        .seconds(
          Math.trunc(timestamp.seconds() / THUMBNAIL_INTERVALS_SECONDS) *
            THUMBNAIL_INTERVALS_SECONDS
        )
        .utc()
        .format("YYYY/MM/DD/HH/YYYY-MM-DDTHH_mm_ss") + "Z";

    const deviceTime = convertISOStrToLocaleTimeStr(
      timezone || "US/Pacific",
      timestamp.toISOString()
    );

    setDisplayTime(deviceTime);
    setTimestampStr(timestampStr);
  }, [s3, realtimeSecondsElapsed]);

  useEffect(() => {
    if (!timestampStr.length) {
      return;
    }

    if (!showImage) {
      return;
    }

    const expirationTime = 3600 * 4;
    const signedUrl = s3.getSignedUrl("getObject", {
      Bucket: process.env.REACT_APP_CloudStorange_BucketName,
      Key: `${accountId}/${serialNumber}/mnt/misc/thumbnails/${timestampStr}.jpg`,
      Expires: expirationTime,
    });

    setNextImgUrl(signedUrl);
  }, [timestampStr, showImage]);

  useEffect(() => {
    if (loading) {
      return;
    }

    if (!nextImgUrl.length) {
      return;
    }

    setLoading(true);
    setImgUrl(nextImgUrl);
  }, [nextImgUrl]);

  const imageLoaded = (evt: any) => {
    imgRef.current.className = classes.image;

    if (evt.target.src != nextImgUrl && nextImgUrl.length) {
      setImgUrl(nextImgUrl);
      setLoading(true);
    } else {
      setLoading(false);
    }
  };

  const imageError = (evt: any) => {
    imgRef.current.className = classes.imageNone;

    if (nextImgUrl.length) {
      setImgUrl(nextImgUrl);
      setLoading(true);
    } else {
      setLoading(false);
    }
  };

  return (
    <>
      {imgUrl.length > 0 && (
        <>
          {showImage && (
            <Grid className={classes.imgWrapper} item>
              <img
                ref={imgRef}
                src={imgUrl}
                className={classes.imagePreload}
                onLoad={imageLoaded}
                onError={imageError}
              />
            </Grid>
          )}
          <Grid className={classes.timeWrapper} item>
            <Typography variant="caption">{displayTime}</Typography>
          </Grid>
        </>
      )}
    </>
  );
};

export default Thumbnail;
